import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminCreateStaffRequest,
  AdminPatchStaffRequest,
} from '@naya/shared'
import { adminApi } from '../adminApi.js'
import type {
  ResetStaffPasswordResponse,
  StaffListResponse,
  StaffRow,
} from '../types.js'

export const staffKey = (tenantId: string) =>
  ['admin', 'tenants', tenantId, 'staff'] as const

interface StaffResponse {
  staff: StaffRow
}

export function useStaff(tenantId: string) {
  return useQuery({
    queryKey: staffKey(tenantId),
    queryFn: () =>
      adminApi.get<StaffListResponse>(`/tenants/${tenantId}/staff`),
  })
}

export function useCreateStaff(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminCreateStaffRequest) =>
      adminApi.post<StaffResponse>(`/tenants/${tenantId}/staff`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKey(tenantId) })
    },
  })
}

export function usePatchStaff(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { staffId: string } & AdminPatchStaffRequest) => {
      const { staffId, ...patch } = vars
      return adminApi.patch<StaffResponse>(
        `/tenants/${tenantId}/staff/${staffId}`,
        patch,
      )
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKey(tenantId) })
    },
  })
}

/**
 * Resets a staff member's password. The response carries the generated
 * temporary password exactly once — the caller shows it in a modal and never
 * refetches it.
 */
export function useResetStaffPassword(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (staffId: string) =>
      adminApi.post<ResetStaffPasswordResponse>(
        `/tenants/${tenantId}/staff/${staffId}/reset-password`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKey(tenantId) })
    },
  })
}
